import type { CrmUser, Project, ProjectUpdate, UserRole } from '../types/crm';
import { canViewAllAccounts } from '../lib/accountVisibility';
import { CRM_TIMEZONE, parseCrmDate } from './dateTime';
import { userCanWriteOwnedRecord, type OwnedRecordAccessOptions } from './crmHelpers';

export function projectByRef(projects: Project[], ref: string): Project | undefined {
  const decoded = decodeURIComponent(ref);
  return projects.find((p) => p.reference_no === decoded);
}

export function updatesForProject(updates: ProjectUpdate[], projectId: string): ProjectUpdate[] {
  return updates
    .filter((u) => u.project_id === projectId)
    .sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''));
}

export function recentUpdatesForProject(
  updates: ProjectUpdate[],
  projectId: string,
  limit?: number,
): ProjectUpdate[] {
  const list = updates
    .filter((u) => u.project_id === projectId)
    .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''));
  return limit != null && limit > 0 ? list.slice(0, limit) : list;
}

export function latestUpdateForProject(
  updates: ProjectUpdate[],
  projectId: string,
): ProjectUpdate | undefined {
  return recentUpdatesForProject(updates, projectId, 1)[0];
}

export function latestUpdatePreview(update?: ProjectUpdate, maxLen = 120): string {
  if (!update) return 'No updates yet.';
  let text = update.content?.trim() || '';
  if (update.attachment_name) {
    text = text ? `${text} 📎` : `📎 ${update.attachment_name}`;
  }
  if (!text) return 'No updates yet.';
  return text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
}

export { formatSingaporeDateTime as formatProjectUpdateTime } from './dateTime';

export function isSystemProjectUpdate(content?: string): boolean {
  const c = content ?? '';
  return c.includes('System') || c.includes('Status changed');
}

export function authorDisplayName(
  update: ProjectUpdate,
  users: CrmUser[],
): string {
  const id = update.author_id ?? update.author_name;
  if (!id || id === 'System') return 'System';
  const user = users.find((u) => u.username === id || u.id === id);
  return user?.display_name?.trim() || user?.username || id;
}

export function authorRoleLabel(users: CrmUser[], authorId?: string): string | null {
  if (!authorId || authorId === 'System') return null;
  const user = users.find((u) => u.username === authorId);
  if (!user?.role || user.role === 'user') return null;
  const labels: Record<string, string> = {
    admin: 'Admin',
    it: 'IT',
    supervisor: 'Supervisor',
    management: 'Management',
  };
  return labels[user.role] ?? user.role;
}

/** Chat alignment: your messages on the right; everyone else (incl. admin/IT) on the left. */
export function isOwnChatMessage(authorId: string | undefined, currentUsername: string): boolean {
  return Boolean(authorId && authorId !== 'System' && authorId === currentUsername);
}

export function userCanAccessProject(
  project: Project | undefined,
  viewRole: UserRole,
  username: string,
  users: CrmUser[],
  teamUsernames: string[] = [],
  options: OwnedRecordAccessOptions = {},
): boolean {
  if (!project) return false;
  if (canViewAllAccounts(viewRole, options.departmentSlug)) {
    return true;
  }
  return userCanWriteOwnedRecord(project.owner_id, viewRole, username, users, teamUsernames, {
    ...options,
    clientId: options.clientId ?? project.client_id,
  });
}

export function projectStatusModifier(status?: string): string {
  if (status === 'Completed') return 'project-status--completed';
  if (status === 'On Hold') return 'project-status--hold';
  return 'project-status--ongoing';
}

/** YYYY-MM of when a project was closed (Completed), never created_at. */
export function projectClosedMonthKey(project: Pick<Project, 'status' | 'completed_date' | 'completed_at'>): string | null {
  if ((project.status ?? '').trim() !== 'Completed') return null;
  const raw = project.completed_date ?? project.completed_at;
  const d = parseCrmDate(raw);
  if (!d) return null;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CRM_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .format(d)
    .slice(0, 7);
}

export type ClosedProjectsMonthBar = {
  month: number;
  count: number;
};

/** Counts Closed projects per calendar month in `year`, keyed by completed_date/completed_at. */
export function buildClosedProjectsMonthBars(
  projects: Pick<Project, 'status' | 'completed_date' | 'completed_at'>[],
  year: number,
): ClosedProjectsMonthBar[] {
  const counts = Array.from({ length: 12 }, () => 0);
  for (const project of projects) {
    const key = projectClosedMonthKey(project);
    if (!key) continue;
    const [y, m] = key.split('-').map(Number);
    if (y !== year || !m || m < 1 || m > 12) continue;
    counts[m - 1] += 1;
  }
  return counts.map((count, i) => ({ month: i + 1, count }));
}

/** Years that have at least one Closed project (by completed date), plus current SG year. */
export function listClosedProjectYears(
  projects: Pick<Project, 'status' | 'completed_date' | 'completed_at'>[],
): number[] {
  const years = new Set<number>();
  const current = Number(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: CRM_TIMEZONE,
      year: 'numeric',
    }).format(new Date()),
  );
  years.add(current);
  for (const project of projects) {
    const key = projectClosedMonthKey(project);
    if (!key) continue;
    const y = Number(key.slice(0, 4));
    if (Number.isFinite(y)) years.add(y);
  }
  return [...years].sort((a, b) => b - a);
}
